import { PaginationTable } from "@/components/ui/pagination-table";
import { EmptyState } from "@/components/EmptyState";
import { Phone } from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileCardView } from "@/components/ui/mobile-card-view";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";

interface CallLog {
  id: string;
  caller_number: string;
  phone_number: string;
  duration: number;
  created_at: string;
  status: string;
  recording_url?: string;
}

const CallLogsTable = () => {
  const isMobile = useIsMobile();
  
  const { data: callLogs, isLoading, error } = useQuery({
    queryKey: ['call-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_calls')
        .select('id,from_number,to_number,started_at,duration_sec,status')
        .order('started_at', { ascending: false })
        .limit(20);

      if (error) {
        throw new Error(error.message);
      }

      return (data || []).map((call: any) => ({
        id: call.id,
        caller_number: call.from_number,
        phone_number: call.to_number,
        created_at: call.started_at,
        duration: call.duration_sec,
        status: call.status,
        recording_url: undefined
      }));
    },
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatDuration = (value: number) => 
    typeof value === 'number' ? `${Math.floor(value / 60)}:${(value % 60).toString().padStart(2, '0')}` : 'N/A';

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-center text-destructive">Error: {error.message}</div>;
  }

  const emptyState = (
    <EmptyState
      icon={<Phone className="w-8 h-8 text-muted-foreground" />}
      title="No call logs yet"
      description="Your call logs will appear here once you start receiving calls through your configured phone numbers."
    />
  );

  if (isMobile) {
    return (
      <MobileCardView
        data={callLogs || []}
        emptyState={emptyState}
        renderCard={(log) => (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{log.caller_number || 'Unknown'}</div>
                <div className="text-sm text-muted-foreground truncate">→ {log.phone_number || 'N/A'}</div>
              </div>
              <Badge className={getStatusColor(log.status)}>
                {log.status || 'Unknown'}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(log.duration)}
              </span>
              <span>{log.created_at ? format(new Date(log.created_at), 'MMM dd, HH:mm') : 'N/A'}</span>
            </div>
          </div>
        )}
      />
    );
  }

  const columns = [
    {
      key: 'caller_number' as keyof CallLog,
      header: 'Caller',
      render: (value: string) => <span className="truncate max-w-[120px] block">{value || 'Unknown'}</span>,
    },
    {
      key: 'phone_number' as keyof CallLog,
      header: 'Phone Number',
      render: (value: string) => <span className="truncate max-w-[120px] block">{value || 'N/A'}</span>,
    },
    {
      key: 'duration' as keyof CallLog,
      header: 'Duration',
      render: (value: number) => formatDuration(value),
    },
    {
      key: 'status' as keyof CallLog,
      header: 'Status',
      render: (value: string) => (
        <Badge className={getStatusColor(value)}>
          {value || 'Unknown'}
        </Badge>
      ),
    },
    {
      key: 'created_at' as keyof CallLog,
      header: 'Date',
      render: (value: string) => 
        value ? format(new Date(value), 'MMM dd, yyyy HH:mm') : 'N/A',
    },
    {
      key: 'recording_url' as keyof CallLog,
      header: 'Recording',
      render: (value: string) => 
        value ? (
          <span className="text-green-600">Available</span>
        ) : (
          <span className="text-muted-foreground">None</span>
        ),
    },
  ];

  return (
    <PaginationTable
      data={callLogs || []}
      columns={columns}
      pageSize={10}
      emptyState={emptyState}
    />
  );
};

export default CallLogsTable;
